import { DispositionType } from 'utils/lib/types/api/chart-data/chart-data.types';
import { PractitionerQualificationCode } from 'utils/lib/types/api/practitioner.types';
import { RadiologyReportType } from 'utils/lib/types/api/radiology';
import { RoleType } from 'utils/lib/types/api/user.types';

export const dataTestIds = {
  closeIcon: 'CloseIcon',
  loadingSpinner: 'loading-spinner',
  loadingScreen: 'loading-screen',
  deleteOutlinedIcon: 'DeleteOutlinedIcon',
  header: {
    userName: 'header-user-name',
    myProfileMenuItem: 'header-my-profile-menu-item',
  },
  myProfilePage: {
    profileTab: 'my-profile-profile-tab',
    notificationsTab: 'my-profile-notifications-tab',
  },
  inPersonHeader: {
    container: 'in-person-header-container',
    patientName: 'patient-name',
    appointmentStatus: 'appointment-status',
    switchModeButton: (status: string) => `switch-status-to-${status}`,
    careTeamButton: 'header-care-team-button',
    careTeamPopover: 'header-care-team-popover',
    intakePractitionerInput: 'intake-practitioner-input',
    providerPractitionerInput: 'provider-practitioner-input',
    changeStatusDropdown: 'change-status-dropdown-in-header',
    allergies: 'header-allergies',
    weight: 'header-weight',
    roomSelect: 'header-room-select',
    payment: 'header-payment',
    reasonForVisit: 'header-reason-for-visit',
  },
  inPersonLayout: {
    selectProviderAlert: 'select-provider-alert',
  },
  dashboard: {
    appointmentsTable: (tab: 'prebooked' | 'in-office' | 'completed' | 'cancelled') => `appointments-table-${tab}`,
    addPatientButton: 'add-patient-button',
    approveButton: 'approve-button',
    completeIntakeButton: 'complete-intake-button',
    intakeButton: `intake-button`,
    readyButton: 'ready-button',
    reviewAndSignButton: 'review-and-sign-button',
    startProviderButton: 'start-provider-button',
    visitDetailsButton: `visit-details-button`,
    progressNoteButton: `progress-note-button`,
    dischargeButton: 'discharge-button',
    prebookedTab: 'prebooked-tab',
    locationSelect: 'location-select',
    dateFilter: 'tracking-board-date-filter',
    datePickerPopover: 'date-picker-popover',
    dateRangeModeCheckbox: 'date-picker-range-mode-checkbox',
    datePickerTodayButton: 'date-picker-today-button',
    datePickerDay: (isoDate: string) => `date-picker-day-${isoDate}`,
    loadingIndicator: 'loading-indicator',
    tableRowWrapper: (appointmentId: string) => `appointments-table-row-${appointmentId}`,
    tableRowStatus: (appointmentId: string) => `appointments-table-row-status-${appointmentId}`,
    tableRowIntakeInput: (appointmentId: string) => `appointments-table-row-intake-input-${appointmentId}`,
    tableRowProviderInput: (appointmentId: string) => `appointments-table-row-provider-input-${appointmentId}`,
    inOfficeTab: 'in-office-tab',
    groupSelect: 'group-select',
    dischargedTab: 'discharged-tab',
    cancelledTab: 'cancelled-tab',
    arrivedButton: 'arrived-button',
    appointmentStatus: 'appointment-status',
    chatButton: 'Chat-outlined-icon',
    patientName: 'patient-name',
    patientFriendlyId: 'patient-friendly-id',
  },
  appointmentPage: {
    patientFullName: 'patient-full-name',
  },
  addPatientPage: {
    pageTitle: 'page-title',
    bookableSelect: 'add-patient-bookable-select',
    mobilePhoneInput: 'mobile-phone-input',
    searchForPatientsButton: 'search-for-patients-button',
    addButton: 'add-button',
    cancelButton: 'cancel-button',
    patientNotFoundButton: 'patient-not-found-button',
    firstNameInput: 'first-name-input',
    lastNameInput: 'last-name-input',
    sexAtBirthDropdown: 'sex-at-birth-dropdown',
    reasonForVisitDropdown: 'reason-for-visit-dropdown',
    visitTypeDropdown: 'visit-type-dropdown',
    serviceCategoryDropdown: 'service-category-dropdown',
    dateFormatValidationError: 'date-format-validation-error',
    prefillForButton: 'prefill-for-button',
    prefilledPatientName: 'prefilled-patient-name',
    prefilledPatientBirthday: 'prefilled-patient-birthday',
    prefilledPatientBirthSex: 'prefilled-patient-birth-sex',
    prefilledPatientEmail: 'prefilled-patient-email',
  },
  pagination: {
    paginationContainer: 'pagination-container',
  },
  patientInformationPage: {
    saveChangesButton: 'save-changes-button',
    addInsuranceButton: 'add-insurance-button',
    breadcrumb: 'breadcrumb',
    pharmacySearch: 'pharmacy-search',
    pharmacySearchDisplay: { text: 'pharmacy-search-display', button: 'pharmacy-search-display-reset-btn' },
  },
  patientHeader: {
    patientId: 'header-patient-id',
    patientName: 'header-patient-name',
    patientBirthSex: 'header-patient-birth-sex',
    patientBirthday: 'header-patient-birthday',
    patientAddress: 'header-patient-address',
    patientPhoneNumber: 'header-patient-phone-number',
    emergencyContact: 'header-patient-emergency-contact',
    closeButton: 'header-patient-close-button',
  },
  insuranceContainer: {
    showMoreButton: 'insurance-show-more-button',
    removeButton: 'insurance-remove-button',
  },
  userSettingsContainer: {
    releaseOfInfoDropdown: 'release-of-info-dropdown',
    RxHistoryConsentDropdown: 'Rx-history-consent-dropdown',
  },

  slots: {
    slot: 'slot',
  },
  addressBook: {
    addNewContactOption: 'address-book-add-new-contact',
    editContactButton: 'address-book-edit-contact',
    contactDialog: 'address-book-contact-dialog',
    saveContactButton: 'address-book-save-contact',
    deleteContactButton: 'address-book-delete-contact',
  },
  faxDialog: {
    root: 'fax-dialog',
    menuItem: 'fax-documents-menu-item',
    recipientName: 'fax-dialog-recipient-name',
    credential: 'fax-dialog-recipient-credential',
    organization: 'fax-dialog-recipient-organization',
    faxNumber: 'fax-dialog-recipient-fax-number',
    senderFax: 'fax-dialog-sender-fax',
    phoneNumber: 'fax-dialog-recipient-phone-number',
    saveAsPcp: 'fax-dialog-save-as-pcp',
    visitCheckbox: 'fax-dialog-visit',
    addRecipient: 'fax-dialog-add-recipient',
    removeRecipient: 'fax-dialog-remove-recipient',
    sendButton: 'fax-dialog-send-button',
    cancelButton: 'fax-dialog-cancel-button',
  },
  faxResultDialog: {
    root: 'fax-result-dialog',
    failedRecipient: 'fax-result-failed-recipient',
    closeButton: 'fax-result-close-button',
  },
  dialog: {
    closeButton: 'dialog-close-button',
    cancelButton: 'dialog-cancel-button',
    proceedButton: 'dialog-proceed-button',
    title: 'dialog-title',
    message: 'dialog-message',
    inPersonModalContent: 'in-person-modal-content',
    alertIcon: 'WarningAmberOutlinedIcon',
  },
  locationConfig: {
    bookingLinks: 'location-booking-links',
    noScheduleWarning: 'location-no-schedule-warning',
    createScheduleLink: 'location-create-schedule-link',
    scheduleLink: (scheduleId: string) => `location-schedule-link-${scheduleId}`,
  },
  virtualLocationsPage: {
    locationsSearch: 'locations-search',
    locationValue: 'location-value',
    operateInLocationValue: 'operate-in-location-value',
    locationRow: (locationId: string) => `location-row-${locationId}`,
  },
  editVirtualLocation: {
    saveChangesButton: 'save-changes-button',
    cancelButton: 'cancel-button',
    operateInLocationToggle: 'operate-in-location-toggle',
    locationNameTitle: 'location-name-title',
    locationNameField: 'location-name-field',
    locationStateField: 'location-state-field',
  },
  patients: {
    addPatientButton: 'patients-add-patient-button',
    searchByLastNameField: 'search-last-name-field',
    searchByGivenNamesField: 'search-given-names-field',
    searchByDateOfBirthField: 'searchByDateOfBirthField',
    searchByPidField: 'search-pid-field',
    searchByPhoneField: 'search-phone-field',
    searchByAddressField: 'search-by-address',
    searchByEmailField: 'search-by-email-field',
    searchByStatusName: 'search-by-status-name',
    searchByLocationName: 'search-by-location-name',
    searchButton: 'search-button',
    resetFiltersButton: 'reset-filters-button',
    patientId: 'patient-id',
    patientName: 'patient-name',
    patientDateOfBirth: 'patient-date-of-birth',
    patientEmail: 'patient-email',
    patientPhoneNumber: 'patient-phone-number',
    patientAddress: 'patient-address',
    searchResultsRowPrefix: 'search-result-row-',
    searchResultRow: (patientId: string) => `${dataTestIds.patients.searchResultsRowPrefix}${patientId}`,
  },
  employeesPage: {
    table: 'employees-content-table',
    searchByName: 'search-by-name-field',
    roleFilter: 'employees-role-filter',
    roleCell: 'employees-role-cell',
    stateFilter: 'employees-state-filter',
    gettingAlertsChip: 'employee-getting-alerts-chip',
    seenPatientRecentlyChip: 'employee-seen-patient-recently-chip',
    informationForm: 'employee-information-form',
    firstName: 'employee-first-name',
    middleName: 'employee-middle-name',
    lastName: 'employee-last-name',
    birthDate: 'employee-birth-date',
    email: 'employee-email',
    phone: 'employee-phone',
    fax: 'employee-fax',
    addressLine1: 'employee-address-line-1',
    addressLine2: 'employee-address-line-2',
    addressCity: 'employee-address-city',
    addressState: 'employee-address-state',
    addressZip: 'employee-address-zip',
    rolesSection: 'employee-roles-section',
    roleEditPermissionHint: 'employee-role-edit-permission-hint',
    roleRow: (employeeRole: RoleType): string => `employee-${employeeRole}-role`,
    providerDetailsCredentialsDropdown: 'employees-provider-details-credentials-dropdown',
    providerDetailsCredentialsOtherText: 'employees-provider-details-credentials',
    providerDetailsNPI: 'employees-provider-details-npi',
    submitButton: 'employees-form-submit-button',
    qualificationsTable: 'employee-qualifications-table',
    qualificationRow: (code: PractitionerQualificationCode): string => `employee-${code}-qualification`,
    deleteQualificationButton: 'employee-delete-qualification-button',
    addQualificationCard: 'add-new-qualification-card',
    newQualificationStateDropdown: 'new-qualification-state-dropdown',
    newQualificationTypeDropdown: 'new-qualification-type-dropdown',
    newQualificationNumberField: 'new-qualification-number-field',
    newQualificationExpDatePicker: 'new-qualification-expiration-date-picker',
    addQualificationButton: 'add-qualification-button',
    deactivateUserButton: 'deactivate-user-button',
    deleteUserButton: 'delete-user-button',
    statusChip: 'employee-status-chip',
    needsReviewChip: 'employee-needs-review-chip',
    employeeRow: (userId: string): string => `employee-row-${userId}`,
  },
  telemedEhrFlow: {
    trackingBoardLocationsSelect: 'telemed-tracking-board-location-select',
    trackingBoardLocationsSelectOption: (id: string) => `telemed-tracking-board-location-select-option-${id}`,
    trackingBoardTableGroupRow: `telemed-tracking-board-table-group-row`,
    trackingBoardTableRow: (appointmentId: string) => `telemed-tracking-board-table-row-${appointmentId}`,
    myPatientsButton: 'telemed-my-patients-button',
    allPatientsButton: 'telemed-all-patients-button',
    trackingBoardTable: 'telemed-tracking-board-table',
    trackingBoardAssignButton: 'telemed-tracking-board-assign-appointment-button',
    trackingBoardViewButton: (appointmentId?: string) =>
      `telemed-tracking-board-view-appointment-button-${appointmentId}`,
    trackingBoardChatButton: (appointmentId?: string) =>
      `telemed-tracking-board-chat-appointment-button-${appointmentId}`,
    appointmentStatusChip: 'telemed-appointment-status-chip',
    footerButtonConnectToPatient: 'telemed-appointment-footer-button-connect-to-patient',
    footerButtonAssignMe: 'telemed-appointment-footer-button-assign-me',
    footerButtonUnassign: 'telemed-appointment-footer-button-unassign',
    appointmentChartFooter: 'telemed-chart-appointment-footer',
    hpiFieldListLoadingSkeleton: 'telemed-hpi-field-list-loading-skeleton',
    hpiCurrentMedicationsPatientProvidedList: 'telemed-hpi-current-medications-patient-provided-list',
    hpiCurrentMedicationsInput: 'telemed-hpi-current-medications-input',
    hpiCurrentMedicationsDoseInput: 'telemed-hpi-current-medications-dose-input',
    hpiCurrentMedicationsDateTimeInput: 'telemed-hpi-current-medications-date-time-input',
    hpiCurrentMedicationsAddButton: 'telemed-hpi-current-medications-add-button',
    hpiCurrentMedicationsScheduledList: 'telemed-hpi-current-medications-scheduled-list',
    hpiCurrentMedicationsAsNeededList: 'telemed-hpi-current-medications-as-needed-list',
    hpiCurrentMedicationsScheduledRadioButton: 'telemed-hpi-current-medications-scheduled-radio-button',
    hpiCurrentMedicationsAsNeededRadioButton: 'telemed-hpi-current-medications-as-needed-radio-button',
    hpiCurrentMedicationsList: (listType: 'scheduled' | 'as-needed') =>
      `telemed-hpi-current-medications-list-${listType}`,
    hpiCurrentMedicationsListItem: (listDataTestId: string) => `${listDataTestId}-item`,
    hpiCurrentMedicationsColumn: 'telemed-hpi-current-medications-column',
    hpiSurgicalHistoryColumn: 'telemed-hpi-surgical-history-column',
    hpiSurgicalHistoryPatientProvidedList: 'telemed-hpi-surgical-history-patient-provided-list',
    hpiAdditionalQuestions: (questionSymptom: string) => `telemed-additional-questions-${questionSymptom}`,
    hpiAdditionalQuestionsPatientProvided: (questionSymptom: string) =>
      `telemed-additional-questions-patient-provided-${questionSymptom}`,
    hpiSurgicalHistoryNote: 'telemed-hpi-surgical-history-note',
    hpiChiefComplaintNotes: 'telemed-chief-complaint-notes',
    hpiChiefComplaintRos: 'telemed-chief-complaint-ros',
    hpiPatientConditionPhotos: 'telemed-patient-condition-photos',
    hpiReasonForVisit: 'telemed-reason-for-visit',
    videoRoomContainer: 'telemed-video-room-container',
    endVideoCallButton: 'telemed-end-video-call-button',
    pinVideoCallButton: 'telemed-pin-video-call-button',
    finishVisitButton: 'telemed-finish-visit-button',
    patientInfoConfirmationCheckbox: 'telemed-patient-info-confirmation-checkbox',
    signButton: 'telemed-sign-button',
    closeChartButton: 'telemed-chart-close-icon',
    planTabDispositionContainer: 'telemed-plan-tab-disposition-container',
    planTabDispositionToggleButton: (buttonName: DispositionType) =>
      `telemed-plan-tab-disposition-toggle-button-${buttonName}`,
    planTabDispositionFollowUpDropdown: 'telemed-plan-tab-disposition-follow-up-dropdown',
    planTabDispositionNote: 'telemed-plan-tab-disposition-note',
    planTabDispositionReasonForTransferDropdown: 'telemed-plan-tab-disposition-reason-for-transfer-dropdown',
    reviewTabMedicationsContainer: 'telemed-review-tab-medications-container',
    reviewTabAdditionalQuestion: (questionSymptom: string) =>
      `telemed-review-tab-additional-question-${questionSymptom}`,
    reviewTabChiefComplaintContainer: 'telemed-review-tab-chief-complaint-container',
    reviewTabRosContainer: 'telemed-review-tab-ros-container',
    reviewTabPatientInstructionsContainer: 'telemed-review-tab-patient-instructions-container',
    reviewTabExaminationsContainer: 'telemed-review-tab-examinations-container',
    examTabTable: 'telemed-exam-tab-table',
    cancelThisVisitButton: 'telemed-cancel-this-visit-button',
    inviteParticipant: 'telemed-invite-participant-button',
    editPatientButtonSideBar: 'telemed-edit-patient-button-side-bar',
    chatModalDescription: 'telemed-chat-modal-description',
    telemedNewOrExistingPatient: 'telemed-new-or-existing-patient',
    hpiSurgicalHistoryAddNoteButton: 'telemed-hpi-surgical-history-add-note-button',
    hpiSurgicalHistoryNoteIsLoading: 'telemed-hpi-surgical-history-note-is-loading',
    closeImagePreviewButton: 'close-image-preview-button',
  },
  sideMenu: {
    completeIntakeButton: 'complete-intake-button',
    sideMenuItem: (item: string): string => `menu-item-${item}`,
  },
  inPersonModal: {
    confirmationDialogue: 'confirmation-dialogue',
  },
  hospitalizationPage: {
    hospitalizationTitle: 'hospitalization-title',
    hospitalizationDropdown: 'hospitalization-dropdown',
    hospitalizationList: 'hospitalization-list',
    deleteIcon: 'DeleteOutlinedIcon',
    addNoteButton: 'add-hospitalization-note-button',
    pencilIconButton: 'EditOutlinedIcon',
  },
  progressNotePage: {
    reviewAndSignButton: 'review-and-sign-button',
    unlockAppointmentButton: 'unlock-appointment-button',
    dischargeButton: 'discharge-button',
    sendFaxButton: 'send-fax-button',
    dischargeSummaryButton: 'discharge-summary-button',
    missingCard: 'missing-card',
    missingCardText: 'missing-card-text',
    patientVerificationLink: 'patient-verification-link',
    primaryDiagnosisLink: 'primary-diagnosis-link',
    secondaryDiagnosisLink: 'secondary-diagnosis-link',
    medicalDecisionLink: 'medical-decision-link',
    emCodeLink: 'em-code-link',
    hpiLink: 'hpi-link',
    accidentDateLink: 'accident-date-link',
    accidentStateLink: 'accident-state-link',
    ccLink: 'chief-complaint-link',
    visitNoteCard: 'visit-note-card',
    procedureItem: 'procedure-item',
    vaccineItem: 'vaccine-item',
    inHouseMedicationItem: 'in-house-medication-item',
    labsTitle: (labType: string) => `title-${labType.toLowerCase().replace(/\s+/g, '-')}`,
    knownAllergiesContainer: 'known-allergies-container',
    inlineEditSection: (section: string) => `inline-edit-section-${section}`,
    inlineEditButton: (section: string) => `inline-edit-button-${section}`,
    inlineEditDoneButton: (section: string) => `inline-edit-done-button-${section}`,
    inlineEditHeader: (section: string) => `inline-edit-header-${section}`,
    medicalConditionsContainer: 'medical-conditions-container',
    surgicalHistoryContainer: 'surgical-history-container',
    additionalQuestions: 'additional-questions',
    supervisorApprovalCheckbox: 'supervisor-approval-checkbox',
    hpiContainer: 'hpi-container',
    moiContainer: 'moi-container',
    accidentContainer: 'accident-container',
    reasonForVisitConfirmed: 'reason-for-visit-confirmed',
    nursingOrdersContainer: 'nursing-orders-container',
    nursingOrderItem: 'nursing-order-item',
    vitalsContainer: 'vitals-container',
    vitalsItem: 'vitals-item',
    hospitalizationContainer: 'hospitalization-container',
    alertIcon: 'WarningAmberOutlinedIcon',
    cptCodes: 'cpt-codes-container',
    rosReviewContainer: 'in-person-progress-note-ros-review-container',
  },
  dischargeDialog: {
    openButton: 'discharge-dialog-open-button',
    printDischargeSummaryCheckbox: 'discharge-dialog-print-discharge-summary-checkbox',
    printWorkNoteCheckbox: 'discharge-dialog-print-work-note-checkbox',
    printSchoolNoteCheckbox: 'discharge-dialog-print-school-note-checkbox',
    printPatientInstructionsCheckbox: 'discharge-dialog-print-patient-instructions-checkbox',
    printProgressNoteCheckbox: 'discharge-dialog-print-progress-note-checkbox',
    signProgressNoteCheckbox: 'discharge-dialog-sign-progress-note-checkbox',
    supervisorApprovalCheckbox: 'discharge-dialog-supervisor-approval-checkbox',
    signDisabledReason: 'discharge-dialog-sign-disabled-reason',
    confirmButton: 'discharge-dialog-confirm-button',
  },
  hpiAndTemplatesPage: {
    hpiTitle: 'hpi-title',
    hpiNotes: 'hpi-notes',
  },
  scribeRecommendations: {
    rail: 'scribe-rail',
    openButton: 'scribe-open-button',
    panel: 'scribe-panel',
    collapseButton: 'scribe-collapse-button',
    resizeHandle: 'scribe-resize-handle',
    transcriptChip: (id: string) => `scribe-transcript-chip-${id}`,
    transcriptPendingChip: 'scribe-transcript-pending-chip',
    transcriptPreview: 'scribe-transcript-preview',
    transcriptToggle: 'scribe-transcript-toggle',
    transcriptSaveButton: 'scribe-transcript-save-button',
    transcriptSaveError: 'scribe-transcript-save-error',
    lockedNotice: 'scribe-locked-notice',
    betaNotice: 'scribe-beta-notice',
    narrativeEditor: 'scribe-narrative-editor',
    narrativeReadView: 'scribe-narrative-read-view',
    narrativeSentence: (index: number) => `scribe-narrative-sentence-${index}`,
    narrativeInput: 'scribe-narrative-input',
    editNarrativeButton: 'scribe-edit-narrative-button',
    narrativeUnbackedNote: 'scribe-narrative-unbacked-note',
    analyzeButton: 'scribe-analyze-button',
    replanDialog: 'scribe-replan-dialog',
    replanConfirmButton: 'scribe-replan-confirm-button',
    replanCancelButton: 'scribe-replan-cancel-button',
    transcriptEvidence: 'scribe-transcript-evidence',
    stage: (name: string) => `scribe-stage-${name}`,
    templateApplyButton: 'scribe-template-apply-button',
    group: (section: string) => `scribe-group-${section}`,
    goToSectionButton: (section: string) => `scribe-go-to-${section}`,
    row: (id: string) => `scribe-row-${id}`,
    rowCheckbox: (id: string) => `scribe-row-checkbox-${id}`,
    rowFinding: (id: string) => `scribe-row-finding-${id}`,
    examLeaf: (id: string) => `scribe-exam-leaf-${id}`,
    examLeafChooser: (id: string) => `scribe-exam-leaf-chooser-${id}`,
    examLeafOption: (id: string, field: string) => `scribe-exam-leaf-option-${id}-${field}`,
    rowText: (id: string) => `scribe-row-text-${id}`,
    rowEditButton: (id: string) => `scribe-row-edit-${id}`,
    rowEditInput: (id: string) => `scribe-row-edit-input-${id}`,
    rowStatus: (id: string) => `scribe-row-status-${id}`,
    rowNote: (id: string) => `scribe-row-note-${id}`,
    rowRetryButton: (id: string) => `scribe-row-retry-${id}`,
    noteModeChip: (id: string) => `scribe-note-mode-chip-${id}`,
    noteModeMenu: (id: string) => `scribe-note-mode-menu-${id}`,
    noteModeOption: (id: string, mode: string) => `scribe-note-mode-option-${id}-${mode}`,
    selectionSummary: 'scribe-selection-summary',
    toggleAllButton: 'scribe-toggle-all-button',
    applyObservationsButton: 'scribe-apply-observations-button',
    aiAddedMark: (id: string) => `scribe-ai-added-${id}`,
    aiAddedSectionChip: 'scribe-ai-added-section-chip',
    notes: 'scribe-notes',
    rejected: 'scribe-rejected',
    pickerDialog: 'scribe-picker-dialog',
    pickerOption: (id: string) => `scribe-picker-option-${id}`,
    pickerSkip: 'scribe-picker-skip',
  },
  assessmentCard: {
    emCodeDropdown: 'em-code-dropdown',
    medicalDecisionField: 'medical-decision-field',
    cptCodeField: 'cpt-code-field',
    medicalDecisionLoading: 'medical-decision-loading',
  },
  diagnosisContainer: {
    diagnosisDropdown: 'diagnosis-dropdown',
    allDiagnosesContainer: 'diagnosis-container-all-diagnoses',
    primaryDiagnosis: 'diagnosis-container-primary-diagnosis',
    secondaryDiagnosisContainer: 'diagnosis-container-secondary-diagnosis-container',
    secondaryDiagnosis: 'diagnosis-container-secondary-diagnosis',
    primaryDiagnosisDeleteButton: 'diagnosis-container-primary-diagnosis-delete-button',
    secondaryDiagnosisDeleteButton: 'diagnosis-container-secondary-diagnosis-delete-button',
    makePrimaryButton: 'diagnosis-container-make-primary-button',
  },
  billingContainer: {
    container: 'billing-container',
    deleteButton: 'billing-container-delete-button',
    deleteCptCodeButton: (code: string) => `billing-container-delete-cpt-code-button-${code}`,
    cptCodeEntry: (code: string) => `cpt-code-entry-${code}`,
    cptCodeContainer: 'additional-cpt-code-container',
  },
  patientInfoPage: {
    patientInfoVerifiedCheckbox: 'patient-info-verified-checkbox',
  },
  inHouseMedicationsPage: {
    title: 'medications-title',
    orderButton: 'order-button',
    marTableLoader: 'mar-table-loader',
    marTable: {
      medicationRowPrefix: 'mar-table-medication-row-',
      medicationRow: (medicationId: string) => `mar-table-medication-row-${medicationId}`,
      medicationCell: 'mar-table-medication-cell',
      statusCell: 'mar-table-status-cell',
      doseCell: 'mar-table-dose-cell',
      routeCell: 'mar-table-route-cell',
      orderedByCell: 'mar-table-ordered-by-cell',
      givenByCell: 'mar-table-given-by-cell',
      reasonCell: 'mar-table-reason-cell',
      instructionsCell: 'mar-table-instructions-cell',
    },
    medicationDetailsTab: 'medication-details-tab',
    marTab: 'mar-tab',
    pencilIconButton: 'EditOutlinedIcon',
    deleteButton: 'DeleteOutlinedIcon',
    administeredButton: 'administered-button',
    partlyAdministeredButton: 'partly-administered-button',
    notAdministeredButton: 'not-administered-button',
    medicationHistoryTableMedication: 'medication-history-table-medication',
    medicationHistoryTableType: 'medication-history-table-type',
    medicationHistoryTableWhoAdded: 'medication-history-table-who-added',
    medicationHistoryTableRow: 'medication-history-table-row',
  },
  orderMedicationPage: {
    inputField: (field: string): string => `input-${field}`,
    fillOrderToSaveButton: 'fill-order-to-save-button',
    backButton: 'back-button',
    confirmationDialogue: 'confirmation-dialogue',
  },
  vitalsPage: {
    title: 'vitals-title',
    deleteVitalModal: 'delete-vital-modal',
    addAllVitalsButton: 'vitals-page-add-all-button',
    temperatureInput: 'temperature-input',
    temperatureHeader: 'temperature-header',
    temperatureAddButton: 'temperature-add-button',
    temperatureItem: 'temperature-item',
    heartbeatHeader: 'heartbeat-header',
    heartbeatInput: 'heartbeat-input',
    heartbeatAddButton: 'heartbeat-add-button',
    heartbeatItem: 'heartbeat-item',
    respirationRateHeader: 'respiration-rate-header',
    respirationRateInput: 'respiration-rate-input',
    respirationRateAddButton: 'respiration-rate-add-button',
    respirationRateItem: 'respiration-rate-item',
    bloodPressureHeader: 'blood-pressure-header',
    bloodPressureSystolicInput: 'blood-pressure-systolic-input',
    bloodPressureDiastolicInput: 'blood-pressure-diastolic-input',
    bloodPressureAddButton: 'blood-pressure-add-button',
    bloodPressureItem: 'blood-pressure-item',
    oxygenSaturationHeader: 'oxygen-saturation-header',
    oxygenSaturationInput: 'oxygen-saturation-input',
    oxygenSaturationAddButton: 'oxygen-saturation-add-button',
    oxygenSaturationItem: 'oxygen-saturation-item',
    weightHeader: 'weight-header',
    weightInput: 'weight-input',
    weightPatientRefusedCheckbox: 'weight-patient-refused-checkbox',
    weightAddButton: 'weight-add-button',
    weightItem: 'weight-item',
    heightHeader: 'height-header',
    heightInput: 'height-input',
    heightTotalInchesInput: 'height-total-inches-input',
    heightFeetInput: 'height-feet-input',
    heightInchRemainderInput: 'height-inch-remainder-input',
    heightAddButton: 'height-add-button',
    heightItem: 'height-item',
    bmiCard: 'bmi-card',
    bmiInfoMessage: 'bmi-info-message',
    bmiWeightRefusedWarning: 'bmi-weight-refused-warning',
    bmiItem: 'bmi-item',
    visionHeader: 'vision-header',
    visionLeftInput: 'vision-left-input',
    visionRightInput: 'vision-right-input',
    visionAddButton: 'vision-add-button',
    visionItem: 'vision-item',
    dotVisionScreeningHeader: 'dot-vision-screening-header',
    dotVisionHorizontalFieldLeftInput: 'dot-vision-horizontal-field-left-input',
    dotVisionHorizontalFieldRightInput: 'dot-vision-horizontal-field-right-input',
    dotVisionAddButton: 'dot-vision-add-button',
    lastMenstrualPeriodHeader: 'last-menstrual-period-header',
    lastMenstrualPeriodDateInput: 'last-menstrual-period-date-input',
    lastMenstrualPeriodUnsureCheckbox: 'last-menstrual-period-unsure-checkbox',
    lastMenstrualPeriodAddButton: 'last-menstrual-period-add-button',
    lastMenstrualPeriodItem: 'last-menstrual-period-item',
    deleteIcon: 'DeleteOutlinedIcon',
    addNoteButton: 'add-vitals-note-button',
    pencilIconButton: 'EditOutlinedIcon',
    abnormalVitalIcon: 'WarningAmberOutlinedIcon',
  },
  vitalsAlertConfig: {
    section: 'vitals-alert-config-section',
    errorSummary: 'vitals-alert-config-error-summary',
    addAgeRangeButton: 'vitals-alert-config-add-age-range-button',
    ageRangeRow: (index: number) => `vitals-alert-config-age-range-row-${index}`,
    removeAgeRangeButton: (index: number) => `vitals-alert-config-remove-age-range-button-${index}`,
    removeAgeRangeDescription: 'vitals-alert-config-remove-age-range-description',
    vitalAccordion: (vital: string) => `vitals-alert-config-accordion-${vital}`,
    thresholdInput: (vital: string, rangeId: string, level: string) =>
      `vitals-alert-config-threshold-${vital}-${rangeId}-${level}`,
    normalRangeCell: (vital: string, rangeId: string) => `vitals-alert-config-normal-range-${vital}-${rangeId}`,
  },
  visitDetailsPage: {
    cancelVisitButton: 'cancel-visit-button',
    cancelationReasonDropdown: 'cancelation-reason-dropdown',
    cancelVisitDialogue: 'cancel-visit-dialogue',
    consentAttestationCheckbox: 'consent-attestation-checkbox',
    consentAttestationSaveButton: 'consent-attestation-save-button',
    consentReminderDialog: 'consent-reminder-dialog',
    actionsMenuButton: 'visit-details-actions-menu-button',
    convertToFollowUpMenuItem: 'convert-to-follow-up-menu-item',
    sendFormMenuItem: 'send-form-menu-item',
    reportIssueMenuItem: 'report-issue-menu-item',
    docsAndDataMenuButton: 'visit-details-docs-and-data-menu-button',
    visitDetailsPdfMenuItem: 'visit-details-pdf-menu-item',
    patientPaperworkPdfMenuItem: 'patient-paperwork-pdf-menu-item',
    allPatientDocsMenuItem: 'all-patient-docs-menu-item',
    legacyDataMenuItem: 'legacy-data-menu-item',
    accessAndChangeLogMenuItem: 'access-and-change-log-menu-item',
    progressNoteButton: 'visit-details-progress-note-button',
    customFormCard: (questionnaireId: string) => `custom-form-card-${questionnaireId}`,
    customFormMenuButton: (questionnaireId: string) => `custom-form-menu-button-${questionnaireId}`,
    customFormEditMenuItem: 'custom-form-edit-menu-item',
    customFormDeleteMenuItem: 'custom-form-delete-menu-item',
    editFormResponseDialog: 'edit-form-response-dialog',
    editFormResponseSaveButton: 'edit-form-response-save-button',
  },
  patientRecordPage: {
    seeAllPatientInfoButton: 'see-all-patient-info-button',
    faxPatientDocsButton: 'fax-patient-docs-button',
    legacyDataButton: 'legacy-data-button',
    medicalRecordButton: 'medical-record-button',
    downloadMedicalRecordArchiveMenuItem: 'download-medical-record-archive-menu-item',
    medicalRecordExportProgress: 'medical-record-export-progress',
    faxMedicalRecordMenuItem: 'fax-medical-record-menu-item',
  },
  patientDocsPage: {
    faxDocumentButton: (documentId: string) => `fax-document-button-${documentId}`,
  },
  addInsuranceDialog: {
    id: 'add-insurance-dialog',
    type: 'add-insurance-dialog-type',
    insuranceCarrier: 'add-insurance-dialog-carrier',
    planType: 'add-insurance-dialog-plan-type',
    memberId: 'add-insurance-dialog-member-id',
    policyHoldersFirstName: 'add-insurance-dialog-policy-holders-first-name',
    policyHoldersLastName: 'add-insurance-dialog-policy-holders-last-name',
    policyHoldersMiddleName: 'add-insurance-dialog-policy-holders-middle-name',
    policyHoldersDateOfBirth: 'add-insurance-dialog-policy-holders-date-of-birth',
    policyHoldersSex: 'add-insurance-dialog-policy-holders-sex',
    streetAddress: 'add-insurance-dialog-street-address',
    addressLine2: 'add-insurance-dialog-address-line2',
    city: 'add-insurance-dialog-city',
    state: 'add-insurance-dialog-state',
    zip: 'add-insurance-dialog-zip',
    relationship: 'add-insurance-dialog-relationship',
    additionalInformation: 'add-insurance-dialog-additional-information',
    addInsuranceButton: 'add-insurance-dialog-add-insurance-button',
  },
  documentProcedurePage: {
    blankPopover: 'blank-popover',
    codingColumn: 'procedure-coding-column',
    title: 'document-procedure-title',
    consentForProcedure: 'consent-for-procedure-checkbox',
    procedureType: 'procedure-type',
    cptCodeInput: 'cpt-code-input',
    cptCode: 'cpt-code',
    recommendedCptCode: (code: string) => `recommended-cpt-code-${code}`,
    cptCodeQuickAddButton: (code: string) => `cpt-code-quick-add-${code}`,
    bestMatchCptCode: 'best-match-cpt-code',
    openCandidatesLine: 'open-candidates-line',
    openCandidatesList: 'open-candidates-list',
    openCandidate: (code: string) => `open-candidate-${code}`,
    codingAssistPanel: 'coding-assist-panel',
    codingAssistLoading: 'coding-assist-loading',
    codingAssistEmpty: 'coding-assist-empty',
    codingAssistFindings: 'coding-assist-findings',
    codingGuidance: 'coding-guidance',
    codingAssistNotAssessed: 'coding-assist-not-assessed',
    codingClinicianReview: 'coding-clinician-review',
    lengthCmInput: 'length-cm-input',
    repairDepthSelect: 'repair-depth-select',
    infusionStartTimeInput: 'infusion-start-time-input',
    infusionStopTimeInput: 'infusion-stop-time-input',
    infusionDurationCaption: 'infusion-duration-caption',
    codingDefenseFindings: 'coding-defense-findings',
    codingDefenseSupported: 'coding-defense-supported',
    codingDefenseNotAssessed: 'coding-defense-not-assessed',
    diagnosis: 'diagnosis',
    diagnosisItem: 'diagnosis-item',
    diagnosisDeleteButton: 'diagnosis-delete-button',
    performedBy: 'performed-by',
    anaesthesia: 'anaesthesia',
    site: 'site',
    sideOfBody: 'side-of-body',
    technique: 'technique',
    instruments: 'instruments',
    procedureDetails: 'procedure-details',
    specimenSent: 'specimen-sent',
    complications: 'complications',
    patientResponse: 'patient-response',
    postProcedureInstructions: 'post-procedure-instructions',
    timeSpent: 'time-spent',
    documentedBy: 'documented-by',
    saveButton: 'save-button',
    procedureTypeInput: 'procedure-type-input',
    procedureDate: 'procedure-date',
    procedureTime: 'procedure-time',
    addDiagnosis: 'add-diagnosis',
  },
  proceduresPage: {
    title: 'procedures-title',
    cptCode: 'cpt-code',
    diagnosis: 'diagnosis',
    procedureType: 'procedure-type',
    documentedBy: 'documented-by',
    procedureRow: 'procedure-row',
  },
  nursingOrdersPage: {
    title: 'nursing-orders-title',
    loading: 'nursing-orders-loading-indicator',
    table: 'nursing-orders-table',
    orderButton: 'nursing-orders-order-button',
    orderRow: 'nursing-orders-row',
    orderNote: 'nursing-orders-row-note',
    status: 'nursing-orders-row-status',
    orderAddedDate: 'nursing-orders-row-date',
    deleteButton: 'nursing-orders-row-delete',
    emptyState: 'nursing-orders-empty-state',
  },
  nursingOrderCreatePage: {
    title: 'nursing-order-create-title',
    orderNoteInput: 'nursing-order-create-note-input',
    orderButton: 'nursing-order-create-order-button',
    cancelButton: 'nursing-order-create-cancel-button',
  },
  nursingOrderDetailsPage: {
    orderNote: 'nursing-order-details-note',
    status: 'nursing-order-details-status',
    historySection: 'nursing-order-details-history',
    historyToggleButton: 'nursing-order-details-history-toggle',
    completeOrderButton: 'nursing-order-details-complete-button',
    backButton: 'nursing-order-details-back-button',
    orderedBy: 'nursing-order-details-ordered-by',
    orderedDate: 'nursing-order-details-ordered-date',
  },
  radioButton: {
    checkedIcon: 'RadioButtonCheckedIcon',
  },
  inHouseLabsPage: {
    title: 'in-house-labs-title',
    orderButton: 'order-button',
    deleteButtonPrefix: 'delete-button-',
    tableRowPrefix: 'in-house-lab-table-row-',
    loading: 'in-house-labs-table-loading',
    noLabsMessage: 'in-house-labs-table-no-labs-text',
  },
  orderInHouseLabPage: {
    title: 'order-in-house-labs-title',
    orderInHouseLabButton: 'order-in-house-lab-button',
    orderAndPrintLabelButton: 'order-and-print-label-button',
    testTypeField: 'test-type',
    testTypeList: 'test-type-list',
    CPTCodeFieldPrefix: 'cpt-code-',
    diagnosis: 'diagnosis',
    additionalDx: 'additionalDx',
    notes: 'in-house-labs-notes',
    loading: 'in-house-lab-orders-loading',
    runAsRepeatBtnPrefix: 'repeat-btn-',
    error: 'order-in-house-lab-error',
  },
  collectSamplePage: {
    testName: 'test-name',
    status: 'order-status',
    source: 'source',
    collectedBy: 'collected-by',
    collectionDate: 'collection-date',
    collectionTime: 'collection-time',
    markCollectedButton: 'mark-collected-button',
  },
  performTestPage: {
    title: 'page-title',
    testName: 'test-name',
    status: 'order-status',
    testResult: (result?: string) => {
      const idPrefix = 'test-result';
      return result ? `${idPrefix}-${result.toLowerCase().replace(/\s+/g, '-')}` : idPrefix;
    },
    submitButton: 'submit-button',
    resultEntryTable: {
      numericPrefix: 'result-numeric-',
    },
  },
  resultPage: {
    testName: 'test-name',
    dateAndStatus: 'order-date-status',
    resultsPDF: 'results-pdf',
    diagnose: 'diagnose',
    repeatBtn: 'repeat-btn',
    resultCard: 'result-card',
    resultPageContainerPrefix: 'result-page-container-',
    reflexAlert: 'reflex-alert',
    orderReflexTestBtn: 'order-reflex-test-btn',
  },
  allergies: {
    allergiesPageTitle: 'allergies-title',
    knownAllergiesColumn: 'known-allergies-column',
    knownAllergiesList: 'known-allergies-list',
    knownAllergiesPatientProvidedList: 'known-allergies-patient-provided-list',
    knownAllergiesInput: 'known-allergies-input',
    knownAllergiesListItem: 'known-allergies-list-item',
    knownAllergiesListItemDeleteButton: 'known-allergies-list-item-delete-button',
  },
  medicalConditions: {
    medicalConditionsPageTitle: 'medical-conditions-title',
    medicalConditionColumn: 'medical-condition-column',
    medicalConditionsList: 'medical-condition-list',
    medicalConditionPatientProvidedList: 'medical-condition-patient-provided-list',
    medicalConditionsInput: 'medical-condition-input',
    medicalConditionListItem: 'medical-condition-list-item',
  },
  surgicalHistory: {
    surgicalHistoryTitle: 'surgical-history-title',
    surgicalHistoryOption: 'surgical-history-option',
    surgicalHistoryColumn: 'surgical-history-column',
    surgicalHistoryList: 'surgical-history-list',
    surgicalHistoryPatientProvidedList: 'surgical-history-patient-provided-list',
    surgicalHistoryInput: 'surgical-history-input',
    surgicalHistoryListItem: 'surgical-history-list-item',
  },
  orderVaccinePage: {
    title: 'order-vaccine-title',
    vaccine: 'order-vaccine-vaccine',
    dose: 'order-vaccine-dose',
    units: 'order-vaccine-units',
    route: 'order-vaccine-route',
    location: 'order-vaccine-location',
    instructions: 'order-vaccine-instructions',
    orderedBy: 'order-vaccine-ordered-by',
    orderVaccineButton: 'order-vaccine-button',
    associatedDx: 'order-vaccine-associated-dx',
    manufacturer: 'order-vaccine-manufacturer',
  },

  immunizationPage: {
    title: 'immunization-title',
    newOrderButton: 'new-order-button',
    marTableRowPrefix: 'mar-table-vaccine-',
    marTableRow: (vaccineId: string) => `mar-table-vaccine-${vaccineId}`,
    marTableVaccineCell: 'mar-table-vaccine-cell',
    marTableStatusCell: 'mar-table-status-cell',
    marTableDoseRouteCell: 'mar-table-dose-route-cell',
    marTableInstructionsCell: 'mar-table-instructions-cell',
    marTableOrderedDateCell: 'mar-table-ordered-date-cell',
    marTableOrderedPersonCell: 'mar-table-ordered-person-cell',
    marTableGivenDateCell: 'mar-table-given-date-cell',
    marTableGivenPersonCell: 'mar-table-given-person-cell',
    marTableReasonCell: 'mar-table-reason-cell',
    vaccineDetailsTab: 'vaccine-details-tab',
    vaccineDetailsCard: 'vaccine-details-card',
    marTab: 'immunization-mar-tab',
    pencilIconButton: 'EditOutlinedIcon',
    deleteButton: 'DeleteOutlinedIcon',
  },

  vaccineDetailsPage: {
    lotNumber: 'lot-number',
    expiredDate: 'expired-date',
    mvxCode: 'mvx-code',
    cvxCode: 'cvx-code',
    cptCode: 'cpt-code',
    ndcCode: 'ndc-code',
    administeredDate: 'administered-date',
    administeredTime: 'administered-time',
    visCheckbox: 'vis-checkbox',
    visGivenDate: 'vis-given-date',
    relationship: 'relationship',
    fullName: 'fullName',
    mobile: 'mobile',
    administeredButton: 'administered-button',
    partlyAdministeredButton: 'partly-administered-button',
    notAdministeredButton: 'not-administered-button',
  },

  administrationConfirmationDialog: {
    patient: 'administration-dialog-patient',
    vaccine: 'administration-dialog-vaccine',
    message: 'administration-dialog-message',
    reasonField: 'administration-dialog-reason-field',
  },

  inHouseMedicationAdministrationConfirmationDialog: {
    patient: 'administration-dialog-patient',
    medication: 'administration-dialog-medication',
    message: 'administration-dialog-message',
    reasonField: 'administration-dialog-reason-field',
  },

  screeningPage: {
    title: 'screening-title',
    asqQuestion: 'asq-question',
    screeningNoteField: 'screening-note-field',
    screeningNoteItem: 'screening-note-item',
    addNoteButton: 'add-screening-note-button',
    askPatientQuestion: (fieldId: string) => `ask-patient-question-${fieldId}`,
    vaccinationNoteField: 'vaccination-note-field',
    answerDropdown: 'answer-dropdown',
  },

  medicationsPage: {
    title: 'medications-title',
    medicationNoteField: 'medication-note-field',
    addNoteButton: 'add-medication-note-button',
    seeMoreButton: 'see-more-button',
    deleteIcon: 'DeleteOutlinedIcon',
    pencilIconButton: 'EditOutlinedIcon',
  },

  updateMedicationPage: {
    medicationDatabaseAlert: 'medication-database-alert',
  },

  editNoteDialog: {
    cancelButton: 'edit-note-dialog-cancel-button',
    proceedButton: 'edit-note-dialog-proceed-button',
    title: 'edit-note-dialog-title',
    message: 'edit-note-dialog-message',
  },

  radiologyPage: {
    title: 'radiology-page-title',
    orderButton: 'radiology-order-button',
    orderMenuButton: 'radiology-order-menu-button',
    externalOrderMenuItem: 'radiology-external-order-menu-item',
    submitOrderButton: 'radiology-submit-order-button',
    performedBySelect: 'radiology-performed-by-select',
    performedByValue: 'radiology-performed-by-value',
    savePerformedByButton: 'radiology-save-performed-by-button',
    markAsReviewedButton: 'radiology-mark-as-reviewed-button',
    editReportButton: (reportType: RadiologyReportType) => `radiology-edit-${reportType}-report-button`,
    editReportInput: (reportType: RadiologyReportType) => `radiology-edit-${reportType}-report-input`,
    saveEditedReportButton: (reportType: RadiologyReportType) => `radiology-save-${reportType}-report-button`,
    radiologyOrderRow: (serviceRequestId: string) => `radiology-order-row-${serviceRequestId}`,
    externalOrderLabel: (serviceRequestId: string) => `radiology-external-order-label-${serviceRequestId}`,
    deleteOrderButton: (serviceRequestId: string) => `radiology-delete-order-${serviceRequestId}`,
  },

  commonLabOrder: {
    deleteDialog: 'delete-lab-order-dialog',
    deleteDialogButton: 'delete-lab-order-dialog-btn',
    labSets: {
      launchModal: 'launch-lab-set-modal-btn',
      selectionModal: 'lab-set-modal',
      selectionBtnPrefix: 'lab-set-select-btn',
    },
  },

  procedureCodes: {
    addButton: 'procedure-codes-add-button',
    searchInput: 'procedure-codes-search-input',
    downloadCsvButton: 'procedure-codes-download-csv-button',
    uploadCsvButton: 'procedure-codes-upload-csv-button',
    codeInput: 'procedure-codes-code-input',
    modifierInput: 'procedure-codes-modifier-input',
    amountInput: 'procedure-codes-amount-input',
    saveButton: 'procedure-codes-save-button',
    cancelButton: 'procedure-codes-cancel-button',
    uploadPreviewDialog: 'procedure-codes-upload-preview-dialog',
    importDeltaButton: 'procedure-codes-import-delta-button',
    replaceAllButton: 'procedure-codes-replace-all-button',
    codeRow: (code: string) => `procedure-code-row-${code}`,
    editButton: (code: string) => `procedure-code-edit-${code}`,
    deleteButton: (code: string) => `procedure-code-delete-${code}`,
  },

  globalTemplates: {
    admin: {
      scanForStaleBtn: 'global-template-scan-staleness-btn',
    },
  },

  adminLabelPrintingConfig: {
    form: 'label-printing-config-form',
    modeSelect: 'label-printing-config-mode-select',
    manufacturerSelect: 'label-printing-config-manufacturer-select',
    labelTypeSelect: 'label-printing-config-label-type-select',
    orientationSelect: 'label-printing-config-orientation-select',
    openPdfOnPrintCheckbox: 'label-printing-config-open-pdf-checkbox',
    submitButton: 'label-printing-config-submit-button',
  },

  reviewOfSystemsPage: {
    rosTableContainer: 'review-of-systems-table-container',
    rosTable: 'review-of-systems-table',
    deniesCheckboxCell: 'ros-denies-checkbox-cell',
    reportsCheckboxCell: 'ros-reports-checkbox-cell',
    selectAllDeniesCell: 'ros-select-all-denies-cell',
    clearRosButton: 'clear-ros-button',
    rosCommentField: (systemKey: string): string => `ros-comment-${systemKey}`,
  },

  examPage: {
    selectAllNormalCheckbox: (sectionKey: string): string => `exam-select-all-normal-${sectionKey}`,
    /** Container of a section's normal findings, i.e. its normal column minus the "Select all". */
    normalFindings: 'exam-normal-findings',
    clearExamButton: 'clear-exam-button',
  },

  externalLabs: {
    labsStatusChip: 'labs-status-chip',
    labsBreadCrumbs: 'labs-bread-crumbs',
    patientRecordLabsTab: 'patient-record-labs-tab',
    labsTable: {
      patientChartExternalLabsPage: 'patient-chart-external-labs',
      patientRecordExternalLabsPage: 'patient-record-external-labs',
      addExternalLabBtn: 'add-external-lab-btn',
      bundleContainerPrefix: 'tables-table-bundle-container-',
      bundleHeaderRowTitlePrefix: 'labs-table-bundle-header-row-title-',
      bundleRowPrefix: 'labs-table-bundle-row-',
      bundleRowCellTestType: 'bundle-row-test-type',
      bundleRowDeleteBtn: 'bundle-row-delete-btn',
      bundleRowSubmitBtn: 'bundle-row-submit-btn',
    },
    createPg: {
      createExternalLabForm: 'create-external-lab-form',
      orderingOffice: 'external-lab-order-office-select',
      additionalDxSelect: 'external-lab-additional-dx-select',
      selectedDxContainer: 'selected-dx-container',
      paymentMethod: 'external-lab-payment-method',
      labsSearchAutoComplete: 'external-lab-search-autocomplete',
      selectedLabContainer: 'selected-external-labs-container',
      addClinicalInfoNote: 'add-clinical-info-note-btn',
      clinicalInfoNote: 'clinical-info-note',
      createExternalLabOrderBtn: 'create-external-lab-order-btn',
    },
    detailsPg: {
      pageContainer: 'externals-lab-detail-pg',
      aoeAnswers: 'external-lab-aoe-answers',
      requisitionNumber: 'external-lab-requisition-number',
      orderingOffice: 'external-lab-ordering-office',
      clinicalNote: 'external-lab-clinical-note',
      aoeTextEntryPrefix: 'external-lab-aoe-text-entry-',
      aoeRadioEntryPrefix: 'external-lab-radio-entry-',
      aoeSingleChoicePrefix: 'external-lab-single-choice-',
      aoeChoiceOptionPrefix: 'external-lab-choice-option-',
      aoeDecimalEntryPrefix: 'external-lab-decimal-entry-',
      samples: {
        card: 'external-lab-sample-collection-card',
        container: 'external-lab-sample-collection-card-container',
        volume: 'external-lab-sample-collection-card-volume',
        minVolume: 'external-lab-sample-collection-card-min-volume',
        storage: 'external-lab-sample-collection-card-storage',
        instructions: 'external-lab-sample-collection-card-instructions',
        collectionDate: 'external-lab-sample-collection-card-collection-date',
        collectionTime: 'external-lab-sample-collection-card-collection-time',
      },
      markReadyBtn: 'external-lab-mark-ready-btn',
    },
  },

  emCodesAdminPage: {
    addButton: 'em-codes-add-button',
    codeRow: (code: string) => `em-code-row-${code}`,
    editButton: (code: string) => `em-code-edit-${code}`,
    deleteButton: (code: string) => `em-code-delete-${code}`,
    dialog: 'em-code-dialog',
    codeField: 'em-code-field',
    displayField: 'em-code-display-field',
    saveButton: 'em-code-save-button',
    confirmDeleteDialog: 'em-code-confirm-delete-dialog',
    confirmDeleteButton: 'em-code-confirm-delete-button',
    cancelDeleteButton: 'em-code-cancel-delete-button',
  },
};
